import type { StartupMaintenanceResult } from '@app/save/startup';

export interface SweepTicket {
  /** この回の通し番号。通知の重複判定に使う。 */
  generation: number;
  /** この回と、相乗りによって予約された再スイープの回収合計。 */
  done: Promise<number>;
}

/**
 * 回収契機を直列化する、React 非依存の調停器。
 *
 * - 同時実行は 1 本だけ
 * - 実行中の契機は「完了後にもう一度」の予約（D-2）
 * - 1 generation の通知権は 1 購読者だけが得る（D-1）
 */
export class StartupSweepCoordinator {
  #inFlight: Promise<number> | null = null;
  #rerunRequested = false;
  #generation = 0;
  #announcedGeneration = 0;

  constructor(private readonly runMaintenance: () => Promise<StartupMaintenanceResult>) {}

  trigger(): SweepTicket {
    if (this.#inFlight) {
      this.#rerunRequested = true;
      return { generation: this.#generation, done: this.#inFlight };
    }

    this.#generation += 1;
    const generation = this.#generation;
    const run = async (): Promise<number> => {
      let recoveredTotal = 0;
      for (;;) {
        this.#rerunRequested = false;
        const { recovered } = await this.runMaintenance();
        recoveredTotal += recovered;
        if (!this.#rerunRequested) return recoveredTotal;
      }
    };

    const done = run().finally(() => {
      this.#inFlight = null;
    });
    this.#inFlight = done;
    return { generation, done };
  }

  /**
   * D-1 の通知権を主張する。去った画面は先に false で抜け、通知権を奪わない。
   */
  claimAnnouncement(generation: number, subscriberCancelled: boolean): boolean {
    if (subscriberCancelled) return false;
    if (this.#announcedGeneration === generation) return false;
    this.#announcedGeneration = generation;
    return true;
  }
}

export interface MaintenanceEventTarget {
  addEventListener(type: string, listener: () => void): void;
  removeEventListener(type: string, listener: () => void): void;
}

/** React/DOM に依存せず、回収契機の購読・解除と通知までを一体で扱う。 */
export function subscribeStartupMaintenance(options: {
  coordinator: StartupSweepCoordinator;
  windowEvents: MaintenanceEventTarget;
  documentEvents: MaintenanceEventTarget;
  isVisible: () => boolean;
  announce: (recovered: number) => void;
}): () => void {
  let cancelled = false;

  const trigger = () => {
    const { generation, done } = options.coordinator.trigger();
    void done
      .then((recovered) => {
        // 去った画面を先に除外してから通知権を主張する（D-1）。
        if (!options.coordinator.claimAnnouncement(generation, cancelled)) return;
        if (recovered > 0) options.announce(recovered);
      })
      // 起動メンテナンスの失敗は次の online/前面復帰で再試行する。未処理 reject にしない。
      .catch(() => undefined);
  };
  const onOnline = () => trigger();
  const onVisible = () => {
    if (options.isVisible()) trigger();
  };

  trigger();
  options.windowEvents.addEventListener('online', onOnline);
  options.documentEvents.addEventListener('visibilitychange', onVisible);

  return () => {
    cancelled = true;
    options.windowEvents.removeEventListener('online', onOnline);
    options.documentEvents.removeEventListener('visibilitychange', onVisible);
  };
}
